import { BASE_CONFIG } from "@/lib/cms";
import { fetcher } from "@/lib/fetcher";
import qs from "qs";
import { useCallback, useMemo } from "react";
import useSWR, { preload as _preload, SWRConfiguration } from "swr";

type Config = RequestInit & {
  [key: string]: any | Config;
};

const cacheRebuild = process.env.NEXT_PUBLIC_FORCE_CACHE_REBUILD || "true";

export default function useGetCMS<T = any>(arg: {
  collection: string;
  key: string;
  config?: Config;
  fallbackData?: Record<string, any>;
  onSuccess?: (data: any) => void;
  swrConfig?: SWRConfiguration;
}) {
  const apiUrl = new URL(
    `${process.env.NEXT_PUBLIC_API_URL}/${arg.collection}`
  );

  const { data, isLoading } = useSWR(
    arg.key,
    () => {
      const { cache, next, ...queryConfig } = arg.config ?? {};
      const query = qs.stringify(
        {
          ...BASE_CONFIG,
          ...(queryConfig ?? {}),
        },
        { encodeValuesOnly: true }
      );

      apiUrl.search = query;

      return fetcher(apiUrl, {
        cache: cache,
        next: next,
        headers: {
          // Authorization: `Bearer ${process.env.NEXT_PUBLIC_API_TOKEN}`,
          "x-cache-rebuild": cacheRebuild,
        },
      });
    },
    {
      fallbackData: arg.fallbackData,
      onSuccess: arg.onSuccess,
      ...arg.swrConfig,
    }
  );

  const preload = useCallback(
    (key: string, config: Record<string, any>) => {
      const query = qs.stringify(
        {
          ...BASE_CONFIG,
          ...(config ?? {}),
        },
        { encodeValuesOnly: true }
      );

      const preloadUrl = new URL(apiUrl);
      preloadUrl.search = query;

      _preload(key, () =>
        fetcher(preloadUrl, {
          headers: {},
        })
      );
    },
    [apiUrl]
  );

  return useMemo(
    () => ({ data: data as T, loading: isLoading, preload }),
    [data, isLoading, preload]
  );
}
